import * as Sentry from '@sentry/browser';
import Cookies from 'js-cookie';
import { getFormData } from '../util/forms';

if (!(window as any).__SENTRY__) {
    Sentry.init({
        allowUrls: [/https?:\/\/(.*\.)?pricewell\.io/],
        dsn: 'https://2e127d31584c4367921b8ef26026a228@o505994.ingest.sentry.io/5595241',
        ignoreErrors: ['t.update is not a function'],
    });
}

function safeParseJson(json?: string): {} {
    try {
        return JSON.parse((json || '').replace(/(['"])?([a-z0-9A-Z_]+)(['"])?:/g, '"$2": '));
    } catch (e) {
        return {};
    }
}

interface Props {
    pageId: string;
    snippetHost: string;
    apiHost: string;
    payButtonClass: string;
    payLinkClass: string;
    toggleCss: string;
    plans: string;
}

export default class PricingPage {
    pageId: string;
    snippetHost: string;
    apiHost: string;
    payButtonClass: string;
    payLinkClass: string;
    toggleCss: string;
    plans: string;
    hasShadowDom = typeof document.body.attachShadow === 'function';

    constructor({ pageId, snippetHost, apiHost, payButtonClass, payLinkClass, toggleCss, plans }: Props) {
        this.pageId = pageId;
        this.snippetHost = snippetHost;
        this.apiHost = apiHost;
        this.payButtonClass = payButtonClass;
        this.payLinkClass = payLinkClass;
        this.toggleCss = toggleCss;
        this.plans = plans;
    }

    updateContent(html: string, shadow: ShadowRoot) {
        const pricewellContainer = document.getElementById('pricewell-' + this.pageId);
        const container = window.document.getElementById('pricewell' + this.pageId);
        const loader = window.document.getElementById('pricewellLoader' + this.pageId);
        let wrapper: HTMLElement;
        let buttons: NodeListOf<Element> | HTMLCollectionOf<Element>;
        let links: NodeListOf<Element> | HTMLCollectionOf<Element>;
        let toggle: HTMLFormElement;
        let plans: NodeListOf<Element> | HTMLCollectionOf<Element>;
        if (loader) {
            loader.remove ? loader.remove() : (loader.style.cssText = 'display:none');
        }
        if (shadow) {
            const div = document.createElement('div');
            div.innerHTML = html;
            shadow.appendChild(div);
            wrapper = shadow.getElementById(this.pageId + '-wrapper');
            buttons = shadow.querySelectorAll('.' + this.payButtonClass);
            links = shadow.querySelectorAll('.' + this.payLinkClass);
            plans = shadow.querySelectorAll('.plan' + this.pageId);
            toggle = shadow.getElementById('toggle' + this.pageId) as HTMLFormElement;
        } else {
            container.innerHTML = html;
            wrapper = window.document.getElementById(this.pageId + '-wrapper');
            buttons = window.document.getElementsByClassName('.' + this.payButtonClass);
            links = window.document.getElementsByClassName('.' + this.payLinkClass);
            plans = window.document.getElementsByClassName('plan' + this.pageId);
            toggle = window.document.getElementById('toggle' + this.pageId) as HTMLFormElement;
        }
        if (!document.getElementById('pricewell-checkout-' + this.pageId)) {
            const embeddedCheckout = document.createElement('div');
            embeddedCheckout.id = 'pricewell-checkout-' + this.pageId;
            pricewellContainer.appendChild(embeddedCheckout);
        }

        const font = wrapper.getAttribute('data-font');
        if (font) {
            const fontLink = document.createElement('link');
            fontLink.href = 'https://fonts.googleapis.com/css?family=' + font;
            fontLink.rel = 'stylesheet';
            window.document.head.appendChild(fontLink);
        }

        if (!window.Stripe || !window.pricewell || !window.pricewell.loaded || Array.isArray(window.pricewell)) {
            const checkoutScript = document.createElement('script');
            checkoutScript.src = `${this.snippetHost}/checkout.js?mode=${wrapper.getAttribute('data-mode')}`;
            checkoutScript.type = 'text/javascript';
            document.head.appendChild(checkoutScript);
        }

        const paypalClientId = wrapper.getAttribute('data-paypal-client-id');
        if (paypalClientId) {
            const paypalScript = document.createElement('script');
            paypalScript.src =
                'https://www.paypal.com/sdk/js?client-id=' +
                paypalClientId +
                '&vault=true&intent=subscription&disable-funding=credit,card';
            paypalScript.type = 'text/javascript';
            document.head.appendChild(paypalScript);
        }

        Array.prototype.forEach.call(buttons, function (button: Element) {
            const handleClick = function () {
                let quantity = pricewellContainer.getAttribute('data-quantity');
                if (!quantity) {
                    quantity = button.getAttribute('data-quantity');
                }
                const isUpgrade = button.getAttribute('data-upgrade') === 'true';
                const upgradeWithoutConfirmation = button.getAttribute('data-upgrade-confirm') !== 'true';
                if (isUpgrade) {
                    button.removeEventListener('click', handleClick);
                    button.innerHTML = 'Please wait...';
                    if (button instanceof HTMLInputElement) {
                        button.value = 'Please wait...';
                        button.disabled = true;
                    }
                    const upgradePortalId = button.getAttribute('data-upgrade-portal');
                    if (upgradePortalId) {
                        window.pricewell.customerPortal(upgradePortalId, {
                            email: pricewellContainer.getAttribute('data-email'),
                            stripeId: pricewellContainer.getAttribute('data-stripe-id'),
                        });
                    } else if (upgradeWithoutConfirmation || window.confirm('Are you sure you want to change plan?')) {
                        const settings = {
                            quantity: quantity,
                            email: pricewellContainer.getAttribute('data-email'),
                            name: pricewellContainer.getAttribute('data-name'),
                            stripeId: pricewellContainer.getAttribute('data-stripe-id'),
                            coupon: pricewellContainer.getAttribute('data-coupon'),
                            chargeLater: pricewellContainer.getAttribute('data-charge-later'),
                        };
                        window.pricewell
                            .upgrade(pageId, button.id, settings)
                            .then(function (res?: { error?: { code?: string } }) {
                                if (res && res.error && res.error.code === 'no_active_payment_method') {
                                    window.pricewell.checkout(
                                        pageId,
                                        button.id,
                                        Object.assign(settings, { chargeLater: true })
                                    );
                                } else {
                                    window.location.reload();
                                }
                            });
                    }
                } else {
                    const inputs = ((button as any).form as HTMLFormElement).getElementsByTagName('input');
                    Array.prototype.forEach.call(inputs, function (input: HTMLInputElement) {
                        if (input.disabled) {
                            input.removeAttribute('disabled');
                            input.setAttribute('data-disabled', 'true');
                        }
                    });
                    const params = getFormData((button as any).form);
                    Array.prototype.forEach.call(inputs, function (input: HTMLInputElement) {
                        if (input.getAttribute('data-disabled')) {
                            input.setAttribute('disabled', 'disabled');
                            input.removeAttribute('data-disabled');
                        }
                    });
                    const addons = ((params.getAll('addons') as string[]) || []).flatMap((a) =>
                        a
                            .split(',')
                            .filter((addon) => addon !== '-') // empty value
                            .map((addon) => {
                                const parts = addon.split('-');
                                if (parts.length > 1) {
                                    return {
                                        id: parts[0],
                                        quantity: parts.length > 1 ? parseInt(parts[1], 10) : 1,
                                    };
                                } else if (parts.length === 1 && parts[0]) {
                                    return {
                                        id: parts[0],
                                        quantity: 1,
                                    };
                                }
                                return null;
                            })
                    );
                    window.pricewell.checkout(pageId, button.id, {
                        quantity: quantity,
                        email: pricewellContainer.getAttribute('data-email'),
                        name: pricewellContainer.getAttribute('data-name'),
                        stripeId: pricewellContainer.getAttribute('data-stripe-id'),
                        coupon: pricewellContainer.getAttribute('data-coupon'),
                        chargeLater: pricewellContainer.getAttribute('data-charge-later'),
                        referral: pricewellContainer.getAttribute('data-client-reference'),
                        addons,
                        meta: { metaId: pricewellContainer.getAttribute('data-meta-id') },
                    });
                }
                window.pricewell.track('checkout', 'page', pageId, button.id);
            };
            button.addEventListener('click', handleClick);
            const paypalPlan = button.getAttribute('data-paypal-plan');
            const successUrl = button.getAttribute('data-success-url');
            if (!!paypalPlan) {
                try {
                    setTimeout(function () {
                        window.paypal
                            .Buttons({
                                style: {
                                    shape: 'pill',
                                    color: 'gold',
                                    layout: 'vertical',
                                    label: 'subscribe',
                                },
                                createSubscription: function (data: any, actions: any) {
                                    return actions.subscription.create({
                                        plan_id: paypalPlan,
                                    });
                                },
                                onApprove: function (data: any, actions: any) {
                                    window.location.href = successUrl || window.location.href;
                                },
                            })
                            .render(
                                shadow
                                    ? shadow.getElementById('paypal-button-container-' + paypalPlan)
                                    : document.getElementById('paypal-button-container-' + paypalPlan)
                            );
                    }, 1000);
                } catch (e) {
                    console.log(e);
                }
            }
        });
        Array.prototype.forEach.call(links, function (link: Element) {
            link.addEventListener('click', function () {
                Cookies.set('pricewell' + pageId, link.id, { expires: 9999 });
            });
        });

        const plansByType: { [key: string]: Element[] } = {};
        Array.prototype.forEach.call(plans, function (plan: Element) {
            if (plan && plan.getAttribute('data-type')) {
                const type = plan.getAttribute('data-type');
                if (plansByType[type]) {
                    plansByType[type].push(plan);
                } else {
                    plansByType[type] = [plan];
                }
            }
        });

        const pageId = this.pageId;

        if (toggle) {
            const classes = this.toggleCss.split(' ');
            toggle.addEventListener('change', function (e) {
                const selectedType = Object.fromEntries(new FormData(toggle))['toggle' + pageId];
                const labels = toggle.getElementsByTagName('label');
                classes.forEach(function (className) {
                    Array.prototype.forEach.call(labels, function (label: HTMLLabelElement) {
                        if (label.htmlFor !== selectedType && label.classList.contains(className)) {
                            label.classList.remove(className);
                        } else if (label.htmlFor === selectedType && !label.classList.contains(className)) {
                            label.classList.add(className);
                        }
                    });
                });
                Object.keys(plansByType).forEach(function (type) {
                    if (type === selectedType) {
                        plansByType[type].forEach(function (plan) {
                            plan.classList.remove('hidden');
                        });
                    } else {
                        plansByType[type].forEach(function (plan) {
                            plan.classList.add('hidden');
                        });
                    }
                });
            });
        }
    }

    getTimeZone() {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.has('timeZone')) {
                return urlParams.get('timeZone');
            }
            return Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch (e) {
            return undefined;
        }
    }

    init() {
        let pricewellContainer = document.getElementById('pricewell-' + this.pageId);
        if (!pricewellContainer) {
            pricewellContainer = document.getElementById('pricewell');
        }
        if (!pricewellContainer) {
            console.log('PriceWell: div with id "pricewell" is missing.');
            return;
        }

        pricewellContainer.className = 'pricewell';

        pricewellContainer.innerHTML = `<div id="pricewell${this.pageId}"></div><img id="pricewellLoader${this.pageId}" src="${this.snippetHost}/loader.gif" alt="" width="32" height="32" class="loader"/>`;

        let shadow: ShadowRoot;
        if (this.hasShadowDom) {
            try {
                shadow = document.getElementById(`pricewell${this.pageId}`).attachShadow({
                    mode: 'open',
                });
                shadow.innerHTML = `<link rel='stylesheet' href='${this.snippetHost}/tailwind.min.css' />`;
            } catch (e) {
                this.hasShadowDom = false;
            }
        }

        const email = pricewellContainer.getAttribute('data-email');
        const metaId = pricewellContainer.getAttribute('data-meta-id');
        const stripeId = pricewellContainer.getAttribute('data-stripe-id');
        const buttonLink = pricewellContainer.getAttribute('data-button-link');
        const buttonOptions = safeParseJson(pricewellContainer.getAttribute('data-button-options'));
        const disableFreePlans = pricewellContainer.getAttribute('data-disable-free-plans');
        const cache = pricewellContainer.getAttribute('data-cache') === 'true';
        const updateContent = this.updateContent.bind(this);
        const getTimeZone = this.getTimeZone.bind(this);
        fetch(this.snippetHost + '/' + this.pageId + '?cache=' + cache, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                email,
                stripeId,
                buttonLink,
                buttonOptions,
                timeZone: getTimeZone(),
                disableFreePlans: !!disableFreePlans,
                metaId,
            }),
        })
            .then(function (res) {
                return res.text();
            })
            .then(function (html) {
                updateContent(html, shadow);
            })
            .catch(function (err) {
                Sentry.captureException(err);
            });
    }
}

window.PricingPage = PricingPage;
